'use client'
import AppIntro from '@/components/apps/AppIntro'

// Mosca: trasladada desde el repo bcm-dev (rama `mosca-contrapunto`, commit 6a2b7434).
// Para actualizarla, comparar contra ese commit en bcm-dev.

import './styles.css'

import { useEffect, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { useParams, useRouter } from 'next/navigation'
import { useTranslation } from 'react-i18next'
import dynamic from 'next/dynamic'

import AppAccessGate from '@/components/apps/AppAccessGate'
import AppTopRightControls from '@/components/apps/AppTopRightControls'
import { AppVolverProvider } from '@/components/apps/AppVolver'
import { sendGA } from '@/lib/analytics'
import { abrirApp } from '@/lib/apps/usoApps'

// Dibuja 139.241 neuronas con WebGL y corre el motor en un Web Worker: sin
// ventana no hay nada que montar, así que no va por SSR.
const MoscaApp = dynamic(() => import('@/components/apps/mosca/MoscaApp'), { ssr: false })

export default function MoscaPage() {
  const { t } = useTranslation()
  const params = useParams()
  const locale = params?.locale || 'es'
  const router = useRouter()

  // Gate por suscripción, igual que BCM Nodes y Grafo de países: no alcanza con
  // haber comprado un libro. Estados: 'checking' | 'allowed' | 'denied'.
  const [accessState, setAccessState] = useState('checking')
  const [gateReason, setGateReason] = useState('product')

  useEffect(() => {
    let cancelled = false
    let unsubscribe = () => {}

    const run = async () => {
      const [{ auth }, { onAuthStateChanged }] = await Promise.all([
        import('@/lib/firebase-client'),
        import('firebase/auth'),
      ])

      if (!auth) {
        if (!cancelled) {
          setAccessState('denied')
          setGateReason('login')
        }
        return
      }

      unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (cancelled) return

        if (!user?.email) {
          setAccessState('denied')
          setGateReason('login')
          return
        }

        try {
          const { apiWithAuth } = await import('@/lib/auth-fetch')
          const res = await apiWithAuth({
            method: 'GET',
            url: 'api/portal/my-subscription',
            params: { email: user.email, _t: Date.now() },
          })
          if (cancelled) return
          if (res?.data?.active) {
            setAccessState('allowed')
          } else {
            setAccessState('denied')
            setGateReason('product')
          }
        } catch (e) {
          console.error('Failed to check subscription:', e)
          if (!cancelled) {
            setAccessState('denied')
            setGateReason('login')
          }
        }
      })
    }

    run()
    return () => {
      cancelled = true
      unsubscribe?.()
    }
  }, [])

  useEffect(() => {
    if (accessState !== 'allowed') return
    sendGA('page_view', {
      page_title: 'Mosca',
      page_location: window.location.href,
      page_path: window.location.pathname,
    })
    return abrirApp('mosca')
  }, [accessState])

  if (accessState === 'checking') {
    return (
      <div className="iframe-layout">
        <div style={{ padding: 16, color: '#bbb' }}><AppIntro>{t('apps:gate.checking_access')}</AppIntro></div>
      </div>
    )
  }

  if (accessState !== 'allowed') {
    return <AppAccessGate appKey="MOSCA" reason={gateReason} />
  }

  const goHome = () => {
    router.push(`/${locale}/apps`)
  }

  return (
    <div className="mosca-shell">
      <AppTopRightControls appKey="MOSCA" />
      <div className="apps-back-button" onClick={goHome}>
        <ArrowLeft color="white" />
      </div>
      <AppVolverProvider value={goHome}>
        <MoscaApp />
      </AppVolverProvider>
    </div>
  )
}
